import React from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { FilterChip } from './FilterChip';
import { Card, SectionTitle } from './ui/Card';
import { useLocale } from '../contexts/LocaleContext';
import { useMetrics } from '../contexts/MetricsContext';
import type { ChartRange, MetricKind, SeriesPoint } from '../types';
import { chartRanges } from '../utils/series';
import { convert, formatMetric } from '../utils/units';
export interface ChartSeries {
  id: string;
  name: string;
  kind: MetricKind;
  points: SeriesPoint[];
  color: string;
}
interface ChartCardProps {
  title: string;
  question: string;
  series: ChartSeries[];
  range: ChartRange;
  onRangeChange: (range: ChartRange) => void;
}
export function ChartCard({
  title,
  question,
  series,
  range,
  onRangeChange
}: ChartCardProps) {
  const {
    t,
    locale
  } = useLocale();
  const {
    unitSystem
  } = useMetrics();
  const dateLocale = locale === 'pt-BR' ? ptBR : undefined;
  const rows = series[0].points.map((point, index) => {
    const row: Record<string, number | string> = {
      label: format(point.date, 'MMM yy', {
        locale: dateLocale
      })
    };
    series.forEach(entry => {
      const value = entry.points[index]?.value ?? 0;
      row[entry.id] = Number(convert(entry.kind, value, unitSystem).toFixed(2));
    });
    return row;
  });
  return <Card className="p-2" data-id="element-516" data-mp="32pmz6rgqibud">
      <SectionTitle data-id="element-517" data-mp="37qj9bmroiu88">{title}</SectionTitle>
      <p className="-mt-1 mb-2 text-[11px] leading-snug text-muted" data-id="element-518" data-mp="37rkkrsclxiqh">{question}</p>

      <ul className="bee-rail -mx-2 flex gap-1 overflow-x-auto px-2 pb-2" data-id="element-519" data-mp="3coul431jbet3">
        {chartRanges.map(item => <li key={item} className="shrink-0" data-id="element-520" data-mp="3hhdvys8u7vu7">
            <FilterChip label={t(`chart.range.${item}`)} active={range === item} onClick={() => onRangeChange(item)} data-id="element-521" data-mp="2k189q8m2f5vo" />
          </li>)}
      </ul>

      <div className="h-[200px] w-full" data-id="element-522" data-mp="2gzdi3spingx3">
        <ResponsiveContainer width="100%" height="100%" data-id="element-523" data-mp="2gy2djqo8y54x">
          <LineChart data={rows} margin={{
          top: 4,
          right: 4,
          bottom: 0,
          left: -18
        }} data-id="element-524" data-mp="2cya47br13uyi">
            <CartesianGrid stroke="var(--bee-line)" vertical={false} data-id="element-525" data-mp="2qpf32bmkoj7s" />
            <XAxis dataKey="label" tick={{
            fontSize: 10,
            fill: 'var(--bee-muted)'
          }} tickLine={false} axisLine={{
            stroke: 'var(--bee-line)'
          }} interval="preserveStartEnd" minTickGap={18} data-id="element-526" data-mp="2qoncjf7u1bod" />
            <YAxis tick={{
            fontSize: 10,
            fill: 'var(--bee-muted)'
          }} tickLine={false} axisLine={false} width={44} data-id="element-527" data-mp="1u0qr9x1g9s6t" />
            <Tooltip contentStyle={{
            borderRadius: 12,
            border: '1px solid var(--bee-line)',
            background: '#fffdf8',
            fontSize: 12
          }} labelStyle={{
            color: 'var(--bee-muted)',
            fontSize: 11
          }} data-id="element-528" data-mp="22z2owj0qhq8r" />
            {series.map(entry => <Line key={entry.id} type="monotone" dataKey={entry.id} name={entry.name} stroke={entry.color} strokeWidth={2} dot={false} activeDot={{
            r: 3
          }} data-id="element-529" data-mp="109kx6dkm2424" />)}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <ul className="mt-2 flex flex-wrap gap-2" data-id="element-530" data-mp="1jw0sg01cf46t">
        {series.map(entry => {
        const last = entry.points[entry.points.length - 1];
        return <li key={entry.id} className="flex items-center gap-1" data-id="element-531" data-mp="1a4d07wwypj4a">
              <span className="h-[3px] w-4 rounded-pill" style={{
            backgroundColor: entry.color
          }} aria-hidden="true" data-id="element-532" data-mp="1f493ftxu56is" />
              <span className="text-[11px] font-medium text-deep" data-id="element-533" data-mp="22s8uyw2z10yf">
                {entry.name}
              </span>
              <span className="bee-num text-[11px] text-muted" data-id="element-534" data-mp="iyqx0cnqfql6">
                {formatMetric(entry.kind, last?.value ?? 0, unitSystem)}
              </span>
            </li>;
      })}
      </ul>
    </Card>;
}

/** Re-exported so existing imports keep working from one palette source. */
export { chartColors } from '../utils/chartTokens';